import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  unlockScroll,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * Breakpoint utilisé par les panneaux autres que le panier.
 * Le panier utilise toujours le mode modal.
 */
const MODAL_BREAKPOINT = 990;

/**
 * Sélecteur des panneaux ouverts :
 * - attribut natif du thème pour les autres panneaux ;
 * - attribut spécifique pour le panier en superposition.
 */
const OPEN_DRAWERS_SELECTOR =
  'theme-drawer[open], theme-drawer[data-pd-overlay-open]';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Compteur d'empilement des panneaux. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(
    `(max-width: ${MODAL_BREAKPOINT - 1}px)`
  );

  /**
   * Identifie uniquement le panier.
   */
  get #isCartDrawer() {
    return this.id === 'cart-drawer';
  }

  /**
   * Le panier est toujours modal.
   * Les autres panneaux conservent leur breakpoint.
   */
  get #usesModal() {
    return this.#isCartDrawer || this.#modalQuery.matches;
  }

  /**
   * @returns {boolean}
   */
  get isOpen() {
    return (
      this.hasAttribute('open') ||
      (
        this.#isCartDrawer &&
        this.hasAttribute('data-pd-overlay-open')
      )
    );
  }

  /**
   * Le panier n'utilise pas [open] sur son élément theme-drawer.
   * Cela évite d'activer les règles CSS de compression associées
   * à theme-drawer[open].
   *
   * Le dialog intérieur conserve son attribut [open] natif,
   * géré par showModal() et close().
   *
   * @param {boolean} open
   */
  #setOpenState(open) {
    if (this.#isCartDrawer) {
      this.toggleAttribute('data-pd-overlay-open', open);
      this.removeAttribute('open');
      return;
    }

    this.toggleAttribute('open', open);
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#isCartDrawer) {
      /*
       * Le conteneur du panier ne crée aucune boîte de mise
       * en page. Le dialog modal est affiché au-dessus du site.
       */
      this.style.setProperty('display', 'contents', 'important');
    }

    this.#modalQuery.addEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    const restoredOrder = Number(
      this.style.getPropertyValue('--drawer-stack-order') || 0
    );

    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restaure les écouteurs d'un panneau déjà ouvert.
   * Convertit un panier restauré en véritable dialog modal.
   */
  #onRestore() {
    const { panel } = this.refs;

    if (this.#isCartDrawer) {
      this.#setOpenState(true);
    }

    if (this.#usesModal) {
      lockScroll(panel);
    }

    if (this.#isCartDrawer) {
      if (!panel.matches(':modal')) {
        if (panel.open) {
          panel.close();
        }

        panel.showModal();
      }

      this.#focusInitialElement();
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    unlockScroll(this.refs.panel);

    this.#modalQuery.removeEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    this.#removeEventListeners();
    removeTrapFocus();

    this.#deferredOpen = false;
  }

  /**
   * Ferme le panneau lors d'un clic sur le fond extérieur.
   *
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean}
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (
      this.refs.panel.querySelector('dialog[open]')
    );
  }

  /**
   * Change le mode des autres panneaux au breakpoint.
   * Le panier reste modal, quelle que soit la largeur.
   */
  #onModalBreakpointChange = () => {
    if (
      this.#isCartDrawer ||
      !this.isOpen ||
      this.#isClosing
    ) {
      return;
    }

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();

    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) &&
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    panel.close();
    removeTrapFocus();

    if (this.#usesModal) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(
        nestedDialog,
        nestedActiveElement
      );
    } else {
      this.#focusInitialElement();
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Ferme le panneau avec la touche Échap.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Ouvre le panneau.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }

    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    this.#previouslyFocused =
      /** @type {HTMLElement | null} */ (
        document.activeElement
      );

    if (this.#usesModal) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      { subtree: false }
    );

    this.#setOpenState(true);
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Ramène un panneau déjà ouvert au premier plan.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    if (this.#usesModal && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      { subtree: false }
    );

    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Place le focus sur le bouton de fermeture.
   */
  #focusInitialElement() {
    const closeButton =
      /** @type {HTMLElement | null} */ (
        this.refs.panel.querySelector(
          '.theme-drawer__close-button'
        )
      );

    closeButton?.focus();
  }

  /**
   * Replace un dialog imbriqué au-dessus du panneau.
   *
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement | null} focusTarget
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (
      focusTarget &&
      document.contains(focusTarget) &&
      dialog.contains(focusTarget)
    ) {
      focusTarget.focus();
    }
  }

  /**
   * Ferme le panneau et restaure le focus ainsi que
   * la position de défilement en mode modal.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;

    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    const closingAsModal = this.#usesModal;

    const scrollTopWhileLocked = closingAsModal
      ? getScrollTop()
      : null;

    this.#setOpenState(false);
    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    if (panel.open) {
      panel.classList.remove(
        'theme-drawer__dialog--opening',
        'theme-drawer__dialog--opening-inline-start'
      );

      panel.classList.add(
        'theme-drawer__dialog--closing'
      );

      await onAnimationEnd(
        panel,
        undefined,
        { subtree: false }
      );

      panel.classList.remove(
        'theme-drawer__dialog--closing'
      );
    }

    panel.close();

    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector(OPEN_DRAWERS_SELECTOR)) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;

    if (trigger && document.contains(trigger)) {
      trigger.focus({
        preventScroll: closingAsModal,
      });
    } else {
      const fallbackTrigger =
        /** @type {HTMLElement | null} */ (
          document.querySelector(
            `[aria-controls="${this.id}"]`
          )
        );

      fallbackTrigger?.focus({
        preventScroll: closingAsModal,
      });
    }

    if (
      scrollTopWhileLocked != null &&
      getScrollTop() !== scrollTopWhileLocked
    ) {
      scrollTo({
        top: scrollTopWhileLocked,
        behavior: 'instant',
      });
    }

    this.#isClosing = false;

    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;

      this.open(options);
    }
  }

  /**
   * Ferme le panneau.
   */
  async close() {
    if (this.#isClosing) {
      this.#deferredOpen = false;
      return;
    }

    await this.#closeDialog();
  }

  /**
   * Ouvre ou ferme le panneau.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }

    return this.open();
  }

  /**
   * @returns {boolean}
   */
  #isTopmost() {
    const myOrder = Number(
      this.style.getPropertyValue('--drawer-stack-order') || 0
    );

    for (
      const other of document.querySelectorAll(
        OPEN_DRAWERS_SELECTOR
      )
    ) {
      if (other === this) continue;

      const otherOrder = Number(
        /** @type {HTMLElement} */ (other)
          .style.getPropertyValue('--drawer-stack-order') || 0
      );

      if (otherOrder > myOrder) {
        return false;
      }
    }

    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, {
      bubbles: true,
    });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, {
      bubbles: true,
    });
  }

  static eventName = 'theme-drawer:close';
}